import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastItem {
  id: number
  message: string
  type: ToastType
  duration: number
}

let nextId = 1

export const useToastStore = defineStore('toast', () => {
  const items = ref<ToastItem[]>([])
  const timers = new Map<number, ReturnType<typeof setTimeout>>()
  const startedAt = new Map<number, number>()
  const remainingMs = new Map<number, number>()
  const paused = new Set<number>()

  function clearTimer(id: number) {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
    startedAt.delete(id)
    remainingMs.delete(id)
    paused.delete(id)
  }

  function scheduleDismiss(id: number, ms: number) {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
    }
    remainingMs.set(id, ms)
    startedAt.set(id, Date.now())
    timers.set(
      id,
      setTimeout(() => dismiss(id), ms),
    )
  }

  function push(message: string, type: ToastType, duration = 4000) {
    // One visible alert at a time — replace any stack so success never sits next to error.
    for (const existing of items.value) {
      clearTimer(existing.id)
    }
    const id = nextId++
    items.value = [{ id, message, type, duration }]
    scheduleDismiss(id, duration)
    return id
  }

  function dismiss(id: number) {
    clearTimer(id)
    items.value = items.value.filter((item) => item.id !== id)
  }

  function pause(id: number) {
    if (paused.has(id)) {
      return
    }
    const timer = timers.get(id)
    if (!timer) {
      return
    }
    const start = startedAt.get(id) ?? Date.now()
    const total = remainingMs.get(id) ?? 0
    const elapsed = Date.now() - start
    const left = Math.max(0, total - elapsed)

    clearTimeout(timer)
    timers.delete(id)
    startedAt.delete(id)
    remainingMs.set(id, left)
    paused.add(id)
  }

  function resume(id: number) {
    if (!paused.has(id)) {
      return
    }
    paused.delete(id)
    const left = remainingMs.get(id) ?? 0
    if (left <= 0) {
      dismiss(id)
      return
    }
    scheduleDismiss(id, left)
  }

  return { items, push, dismiss, pause, resume }
})
